import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * MagicButterfly — a graceful, fluttering butterfly that drifts across the
 * viewport with a soft trail of gold particles. Companion to MagicHummingbird.
 *
 * Differences from the hummingbird:
 *  - Slower, more drifting flight (butterflies don't dart; they float).
 *  - Visible wing flap (200ms cycle) rendered via CSS scaleX animation.
 *  - Warmer palette: gold + soft rose for a richer dust trail.
 *  - Less frequent appearances (long quiet intervals between spawns).
 */
const COLORS = ["#FFE9A8", "#F6D274", "#D4AF37", "#F2D6D6", "#E8B4A2"];
const SKIP_PATTERNS = [/^\/admin/, /^\/auth/, /^\/checkout/];

const rand = (a, b) => Math.random() * (b - a) + a;
const randInt = (a, b) => Math.floor(rand(a, b + 1));
const lerp = (a, b, t) => a + (b - a) * t;
const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);

// Butterfly SVG with two wings that can flap. The wings have id="left"/"right"
// so the CSS animation in index.css can target them. Painted on transparent
// background, gold/rose iridescent gradient.
const BUTTERFLY_SVG = `
<svg viewBox="0 0 200 160" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
  <defs>
    <radialGradient id="wingGrad" cx="50%" cy="50%" r="60%">
      <stop offset="0%" stop-color="#FFF6D8" stop-opacity="0.98"/>
      <stop offset="35%" stop-color="#FFE9A8" stop-opacity="0.95"/>
      <stop offset="65%" stop-color="#D4AF37" stop-opacity="0.88"/>
      <stop offset="100%" stop-color="#7A4A1E" stop-opacity="0.6"/>
    </radialGradient>
    <radialGradient id="wingGrad2" cx="50%" cy="50%" r="60%">
      <stop offset="0%" stop-color="#FFE9D5" stop-opacity="0.95"/>
      <stop offset="50%" stop-color="#E8B4A2" stop-opacity="0.85"/>
      <stop offset="100%" stop-color="#7A1F2B" stop-opacity="0.55"/>
    </radialGradient>
    <filter id="wingGlow" x="-20%" y="-20%" width="140%" height="140%">
      <feGaussianBlur stdDeviation="1.4" result="b"/>
      <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
    </filter>
  </defs>
  <!-- Left wing pair -->
  <g id="bw-left" style="transform-origin: 100px 80px;" filter="url(#wingGlow)">
    <path d="M100 80 C 60 30, 18 28, 8 60 C 2 80, 22 96, 60 92 C 78 90, 92 86, 100 80 Z" fill="url(#wingGrad)"/>
    <path d="M100 80 C 70 96, 28 110, 16 128 C 8 142, 30 150, 60 138 C 82 130, 96 110, 100 80 Z" fill="url(#wingGrad2)"/>
    <circle cx="40" cy="62" r="4" fill="#7A4A1E" opacity="0.55"/>
    <circle cx="56" cy="78" r="2.5" fill="#FFFFFF" opacity="0.55"/>
  </g>
  <!-- Right wing pair (mirrored) -->
  <g id="bw-right" style="transform-origin: 100px 80px;" filter="url(#wingGlow)">
    <path d="M100 80 C 140 30, 182 28, 192 60 C 198 80, 178 96, 140 92 C 122 90, 108 86, 100 80 Z" fill="url(#wingGrad)"/>
    <path d="M100 80 C 130 96, 172 110, 184 128 C 192 142, 170 150, 140 138 C 118 130, 104 110, 100 80 Z" fill="url(#wingGrad2)"/>
    <circle cx="160" cy="62" r="4" fill="#7A4A1E" opacity="0.55"/>
    <circle cx="144" cy="78" r="2.5" fill="#FFFFFF" opacity="0.55"/>
  </g>
  <!-- Body -->
  <ellipse cx="100" cy="80" rx="3.5" ry="22" fill="#3a2a14"/>
  <!-- Antennae -->
  <path d="M99 60 C 94 50, 88 44, 84 38" stroke="#3a2a14" stroke-width="1.2" fill="none" stroke-linecap="round"/>
  <path d="M101 60 C 106 50, 112 44, 116 38" stroke="#3a2a14" stroke-width="1.2" fill="none" stroke-linecap="round"/>
  <circle cx="84" cy="38" r="1.5" fill="#D4AF37"/>
  <circle cx="116" cy="38" r="1.5" fill="#D4AF37"/>
</svg>
`;

// Build a parametric butterfly path — gentle drifting / spiraling, never darting.
const buildPath = (W, H) => {
  const fromLeft = Math.random() > 0.5;
  const sx = fromLeft ? -120 : W + 120;
  const ex = fromLeft ? W + 120 : -120;
  const sy = rand(H * 0.18, H * 0.65);
  const ey = rand(H * 0.20, H * 0.65);
  const dir = fromLeft ? 1 : -1;

  // Smooth drift across with multi-frequency sinusoidal bobbing — feels alive.
  const f1 = rand(2.2, 3.4);
  const f2 = rand(1.0, 1.6);
  const amp1 = rand(H * 0.06, H * 0.12);
  const amp2 = rand(H * 0.02, H * 0.05);

  return {
    duration: rand(11000, 16000), // butterflies are slow — 11-16s per crossing
    dir,
    fn: (t) => {
      const u = easeInOut(t);
      const x = lerp(sx, ex, u);
      const baseY = lerp(sy, ey, u);
      const bob = Math.sin(t * Math.PI * f1) * amp1 + Math.cos(t * Math.PI * f2) * amp2;
      return { x, y: baseY + bob };
    },
  };
};

const MagicButterfly = () => {
  const stageRef = useRef(null);
  const loc = useLocation();
  const stateRef = useRef({ raf: 0, timer: 0 });

  useEffect(() => {
    if (SKIP_PATTERNS.some((re) => re.test(loc.pathname))) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const stage = stageRef.current;
    if (!stage) return undefined;

    let cancelled = false;

    const spawnParticle = (x, y, opts = {}) => {
      const el = document.createElement("span");
      const size = opts.size ?? rand(2, 5);
      const color = COLORS[randInt(0, COLORS.length - 1)];
      el.className = "magic-particle";
      el.style.left = `${x}px`;
      el.style.top = `${y}px`;
      el.style.width = `${size}px`;
      el.style.height = `${size}px`;
      el.style.background = color;
      el.style.boxShadow = `0 0 ${size * 2.2}px ${color}`;
      const dx = opts.dx ?? rand(-24, 24);
      const dy = opts.dy ?? rand(-40, -6);
      const dur = opts.duration ?? rand(1100, 2200);
      el.style.setProperty("--dx", `${dx}px`);
      el.style.setProperty("--dy", `${dy}px`);
      el.style.setProperty("--dur", `${dur}ms`);
      stage.appendChild(el);
      setTimeout(() => el.remove(), dur + 80);
    };

    const burst = (x, y, count) => {
      for (let i = 0; i < count; i += 1) {
        const a = (Math.PI * 2 * i) / count + rand(-0.3, 0.3);
        const r = rand(14, 50);
        spawnParticle(x, y, {
          dx: Math.cos(a) * r,
          dy: Math.sin(a) * r - rand(0, 14),
          duration: rand(900, 1600),
          size: rand(2, 4.5),
        });
      }
    };

    const flight = () => {
      if (cancelled) return;

      const W = window.innerWidth;
      const H = window.innerHeight;
      const path = buildPath(W, H);

      const wrap = document.createElement("div");
      wrap.className = "magic-butterfly";
      wrap.innerHTML = BUTTERFLY_SVG;
      stage.appendChild(wrap);

      const size = Math.max(54, Math.min(96, W * 0.055));
      wrap.style.width = `${size}px`;
      wrap.style.height = `${size * 0.8}px`;
      wrap.style.opacity = "0";

      const startedAt = performance.now();
      const materialise = 700;
      const dematerialise = 800;

      const p0 = path.fn(0);
      burst(p0.x, p0.y, 12);

      let prev = { x: p0.x, y: p0.y };
      let lastTrailAt = 0;

      const tick = (now) => {
        if (cancelled) return;
        const elapsed = now - startedAt;
        const t = Math.min(1, elapsed / path.duration);
        const pt = path.fn(t);

        const vx = pt.x - prev.x;
        const vy = pt.y - prev.y;
        const angle = Math.atan2(vy, vx) * (180 / Math.PI);
        prev = { x: pt.x, y: pt.y };

        // Butterflies tilt softly with the wind. Clamp gently.
        const tilt = Math.max(-22, Math.min(22, angle * 0.4));
        const visualAngle = path.dir > 0 ? tilt : 180 - tilt;

        // Materialise / dematerialise
        let op = 0.95;
        let scale = 1;
        if (elapsed < materialise) {
          const u = elapsed / materialise;
          op = 0.95 * u;
          scale = 0.5 + 0.5 * u;
        } else if (elapsed > path.duration - dematerialise) {
          const u = Math.max(0, (path.duration - elapsed) / dematerialise);
          op = 0.95 * u;
          scale = 0.6 + 0.4 * u;
        }

        wrap.style.opacity = String(op);
        wrap.style.transform = `translate(${pt.x - size / 2}px, ${pt.y - (size * 0.8) / 2}px) rotate(${visualAngle}deg) scale(${scale})`;

        // Gentle continuous dust trail (slower emit cadence than hummingbird)
        const emitGap = 110;
        if (elapsed - lastTrailAt > emitGap && t > 0.04 && t < 0.96) {
          lastTrailAt = elapsed;
          spawnParticle(pt.x + rand(-8, 8), pt.y + rand(-6, 12), {
            dx: rand(-8, 8),
            dy: rand(-22, -2),
            duration: rand(1100, 2000),
            size: rand(1.4, 3.8),
          });
          if (Math.random() < 0.35) {
            spawnParticle(pt.x + rand(-12, 12), pt.y + rand(-8, 14), {
              dx: rand(-14, 14),
              dy: rand(-18, 2),
              duration: rand(900, 1500),
              size: rand(1, 2.8),
            });
          }
        }

        if (t < 1) {
          stateRef.current.raf = requestAnimationFrame(tick);
        } else {
          burst(pt.x, pt.y, 14);
          wrap.remove();
          if (cancelled) return;
          // Long quiet between butterfly appearances (less is more)
          stateRef.current.timer = window.setTimeout(flight, randInt(26000, 48000));
        }
      };

      stateRef.current.timer = window.setTimeout(() => {
        stateRef.current.raf = requestAnimationFrame(tick);
      }, 320);
    };

    const onVisibility = () => {
      if (document.hidden) {
        clearTimeout(stateRef.current.timer);
        cancelAnimationFrame(stateRef.current.raf);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    // First appearance staggered after the kolibri so they don't collide
    stateRef.current.timer = window.setTimeout(flight, randInt(8000, 14000));

    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisibility);
      clearTimeout(stateRef.current.timer);
      cancelAnimationFrame(stateRef.current.raf);
      while (stage.firstChild) stage.removeChild(stage.firstChild);
    };
  }, [loc.pathname]);

  return <div ref={stageRef} className="magic-stage" aria-hidden="true" data-testid="magic-butterfly-stage" />;
};

export default MagicButterfly;
